/**
 * Apresentação aos condôminos (0.1.77) — slides de prestação de contas com IA.
 *
 * O síndico escolhe o período e, se quiser, tópicos (até 10) e material extra
 * (arquivo PDF/Word/texto, documento da Biblioteca ou texto colado). A IA escreve
 * o roteiro; o visual e os números vêm do sistema (backend `apresentacao/`).
 * Depois: prévia, "Pedir alteração" (IA), inserir itens (IA), excluir slide e
 * voltar versão (sem IA), baixar o HTML ou salvar em PDF.
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, ArrowLeft, CheckCircle2, Download, ExternalLink, FileText, History, Library,
  Loader2, Plus, Presentation, RotateCcw, Sparkles, Trash2, Upload, Wand2, X,
} from "lucide-react";
import { api, apresentacaoHtmlUrl, ApiError } from "../lib/api";
import { fmtDataHora } from "../lib/datas";
import { usePermissoes } from "../lib/permissoes";

interface Props {
  condominioId: number;
  onVoltar: () => void;
}

interface ItemConferencia { tipo: "problema" | "aviso"; slide: number | null; texto: string }
interface Versao {
  numero: number;
  origem: string;
  origem_rotulo: string;
  pedido: string | null;
  topicos: string[];
  documentos: { nome: string; origem: string; caracteres: number }[];
  conferencia: ItemConferencia[];
  uso: { modelo?: string; segundos?: number; custo_estimado_usd?: number };
  n_slides: number;
  slides: string[];
  criado_por: string | null;
  criado_em: string;
}
interface Resumo {
  id: number;
  titulo: string;
  meses: number;
  periodo_txt: string | null;
  status: "gerando" | "pronta" | "erro";
  tarefa: string | null;
  erro: string | null;
  n_versoes: number;
  criado_por: string | null;
  criado_em: string;
  atualizado_em: string;
}
interface Detalhe extends Resumo { condominio_id: number; versoes: Versao[] }
interface Lista { condominio_id: number; meses_auditados: number; tem_chave_ia: boolean; itens: Resumo[] }
interface DocBiblioteca { id: number; titulo: string; categoria: string }

const MAX_ITENS = 10;
const OPCOES_MESES = [1, 2, 3, 4, 6, 12];

function msgErro(e: unknown): string {
  return e instanceof ApiError ? e.message : "Algo deu errado. Tente de novo.";
}

// ---------------------------------------------------------------------
// Material extra: tópicos + arquivos + Biblioteca + texto colado
// ---------------------------------------------------------------------
interface Material { topicos: string[]; arquivos: File[]; biblioteca: number[]; texto: string }
const MATERIAL_VAZIO: Material = { topicos: [""], arquivos: [], biblioteca: [], texto: "" };

function contarItens(m: Material): number {
  return m.topicos.filter((t) => t.trim()).length + m.arquivos.length + m.biblioteca.length + (m.texto.trim() ? 1 : 0);
}

function paraForm(m: Material, fd: FormData): FormData {
  fd.append("topicos", JSON.stringify(m.topicos.map((t) => t.trim()).filter(Boolean)));
  fd.append("biblioteca_ids", JSON.stringify(m.biblioteca));
  fd.append("texto_extra", m.texto);
  m.arquivos.forEach((a) => fd.append("arquivos", a));
  return fd;
}

function EditorMaterial({ condominioId, valor, onChange }: {
  condominioId: number; valor: Material; onChange: (m: Material) => void;
}) {
  const inputArq = useRef<HTMLInputElement>(null);
  const { podeLer } = usePermissoes();
  const verBiblioteca = podeLer(condominioId, "biblioteca");
  const { data: bib } = useQuery<{ itens: DocBiblioteca[] }>({
    queryKey: ["biblioteca-apresentacao", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/biblioteca`),
    enabled: verBiblioteca,
  });
  const total = contarItens(valor);
  const cheio = total >= MAX_ITENS;
  const set = (p: Partial<Material>) => onChange({ ...valor, ...p });

  return (
    <div className="space-y-4">
      <div>
        <div className="text-sm font-semibold text-slate-800 mb-1">Tópicos que você quer na apresentação</div>
        <p className="text-xs text-slate-500 mb-2">Um assunto por linha (ex.: "Explicar por que a Conta Principal ficou negativa em julho").</p>
        <div className="space-y-1.5">
          {valor.topicos.map((t, i) => (
            <div key={i} className="flex gap-1.5">
              <span className="w-6 pt-2 text-xs text-slate-400 text-right">{i + 1}.</span>
              <input value={t} maxLength={300}
                onChange={(e) => set({ topicos: valor.topicos.map((x, k) => (k === i ? e.target.value : x)) })}
                placeholder="Escreva um tópico"
                className="flex-1 px-3 py-1.5 text-sm border border-slate-300 rounded-md" />
              {valor.topicos.length > 1 && (
                <button type="button" title="Remover" onClick={() => set({ topicos: valor.topicos.filter((_, k) => k !== i) })}
                  className="px-2 text-slate-400 hover:text-rose-600"><X size={15} /></button>
              )}
            </div>
          ))}
        </div>
        {!cheio && (
          <button type="button" onClick={() => set({ topicos: [...valor.topicos, ""] })}
            className="mt-1.5 ml-7 inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline">
            <Plus size={13} /> Mais um tópico
          </button>
        )}
      </div>

      <div>
        <div className="text-sm font-semibold text-slate-800 mb-1">Textos longos (opcional)</div>
        <p className="text-xs text-slate-500 mb-2">
          Memorial de obra, comunicado, apresentação antiga… A IA resume cada um em 1 ou 2 slides,
          marcados como "Informe do síndico" (não são dados auditados).
        </p>
        <div className="flex flex-wrap gap-2 mb-2">
          <input ref={inputArq} type="file" multiple accept=".pdf,.docx,.txt,.md" className="hidden"
            onChange={(e) => {
              const novos = Array.from(e.target.files ?? []);
              set({ arquivos: [...valor.arquivos, ...novos].slice(0, 5) });
              e.target.value = "";
            }} />
          <button type="button" disabled={cheio} onClick={() => inputArq.current?.click()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50">
            <Upload size={15} /> Anexar arquivo (PDF, Word ou texto)
          </button>
        </div>
        {valor.arquivos.length > 0 && (
          <ul className="mb-2 space-y-1">
            {valor.arquivos.map((a, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                <FileText size={14} className="text-slate-400" /> {a.name}
                <button type="button" onClick={() => set({ arquivos: valor.arquivos.filter((_, k) => k !== i) })}
                  className="text-slate-400 hover:text-rose-600"><X size={14} /></button>
              </li>
            ))}
          </ul>
        )}
        {verBiblioteca && (bib?.itens?.length ?? 0) > 0 && (
          <details className="mb-2">
            <summary className="cursor-pointer text-sm text-brand-700 inline-flex items-center gap-1">
              <Library size={14} /> Escolher da Biblioteca ({valor.biblioteca.length} marcado(s))
            </summary>
            <div className="mt-2 max-h-48 overflow-auto border border-slate-200 rounded-md p-2 space-y-1">
              {bib!.itens.map((d) => {
                const marcado = valor.biblioteca.includes(d.id);
                return (
                  <label key={d.id} className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={marcado} disabled={!marcado && cheio}
                      onChange={() => set({ biblioteca: marcado ? valor.biblioteca.filter((x) => x !== d.id) : [...valor.biblioteca, d.id] })} />
                    {d.titulo} <span className="text-xs text-slate-400">· {d.categoria}</span>
                  </label>
                );
              })}
            </div>
          </details>
        )}
        <textarea value={valor.texto} onChange={(e) => set({ texto: e.target.value })} rows={4}
          placeholder="…ou cole aqui um texto (ex.: os itens da obra, um comunicado)"
          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md" />
      </div>
      <p className={`text-xs ${cheio ? "text-amber-700 font-medium" : "text-slate-400"}`}>
        {total} de {MAX_ITENS} itens (tópicos + textos).
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------
export function ApresentacoesPage({ condominioId, onVoltar }: Props) {
  const qc = useQueryClient();
  const { podeEscrever } = usePermissoes();
  const podeEditar = podeEscrever(condominioId, "apresentacoes");
  const [abertaId, setAbertaId] = useState<number | null>(null);

  const { data: lista, isLoading } = useQuery<Lista>({
    queryKey: ["apresentacoes", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/apresentacoes`),
    refetchInterval: (q) => (q.state.data?.itens.some((i) => i.status === "gerando") ? 4000 : false),
  });

  if (abertaId != null) {
    return <DetalheApresentacao id={abertaId} podeEditar={podeEditar} condominioId={condominioId}
      onVoltar={() => { setAbertaId(null); qc.invalidateQueries({ queryKey: ["apresentacoes", condominioId] }); }} />;
  }

  return (
    <div>
      <button type="button" onClick={onVoltar}
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft size={16} /> Voltar
      </button>
      <div className="flex items-center gap-2 mb-1">
        <Presentation size={22} className="text-brand-700" />
        <h2 className="text-2xl font-semibold text-slate-900">Apresentação aos condôminos</h2>
      </div>
      <p className="text-sm text-slate-500 mb-5 max-w-3xl">
        Slides de prestação de contas prontos para a assembleia ou o grupo do prédio, a partir dos
        balancetes auditados. A IA escreve o roteiro; os números e gráficos vêm direto do sistema e
        são conferidos. Você revisa, pede ajustes e baixa.
      </p>

      {lista && !lista.tem_chave_ia && (
        <Aviso cor="amber">Não há chave de IA configurada para este condomínio. Fale com o Dono do sistema.</Aviso>
      )}
      {lista && lista.meses_auditados === 0 && (
        <Aviso cor="amber">Este condomínio ainda não tem meses auditados para apresentar.</Aviso>
      )}

      {podeEditar && lista && lista.tem_chave_ia && lista.meses_auditados > 0 && (
        <NovaApresentacao condominioId={condominioId} mesesAuditados={lista.meses_auditados}
          onCriada={(id) => { qc.invalidateQueries({ queryKey: ["apresentacoes", condominioId] }); setAbertaId(id); }} />
      )}

      <h3 className="text-sm font-semibold text-slate-700 mt-6 mb-2">Apresentações geradas</h3>
      {isLoading && <Loader2 className="animate-spin text-slate-400" />}
      {lista && lista.itens.length === 0 && <p className="text-sm text-slate-400">Nenhuma ainda.</p>}
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {lista?.itens.map((a) => (
          <button key={a.id} type="button" onClick={() => setAbertaId(a.id)}
            className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-brand-500 hover:shadow-sm transition">
            <div className="font-medium text-slate-900 line-clamp-2">{a.titulo}</div>
            <div className="text-xs text-slate-500 mt-1">{a.periodo_txt ?? `últimos ${a.meses} meses`}</div>
            <div className="mt-2 flex items-center gap-2 text-xs">
              <StatusPill status={a.status} />
              <span className="text-slate-400">{a.n_versoes} versão(ões) · {fmtDataHora(a.atualizado_em)}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function Aviso({ cor, children }: { cor: "amber" | "rose" | "emerald"; children: React.ReactNode }) {
  const cls = {
    amber: "border-amber-500 bg-amber-50 text-amber-900",
    rose: "border-rose-500 bg-rose-50 text-rose-900",
    emerald: "border-emerald-500 bg-emerald-50 text-emerald-900",
  }[cor];
  return <div className={`mb-4 rounded-xl border-l-4 px-4 py-3 text-sm ${cls}`}>{children}</div>;
}

function StatusPill({ status }: { status: Resumo["status"] }) {
  if (status === "gerando")
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-sky-100 text-sky-800 font-medium"><Loader2 size={11} className="animate-spin" /> gerando</span>;
  if (status === "erro")
    return <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-800 font-medium">erro</span>;
  return <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-medium">pronta</span>;
}

function NovaApresentacao({ condominioId, mesesAuditados, onCriada }: {
  condominioId: number; mesesAuditados: number; onCriada: (id: number) => void;
}) {
  const opcoes = OPCOES_MESES.filter((m) => m <= Math.max(1, mesesAuditados));
  const [meses, setMeses] = useState(Math.min(3, opcoes[opcoes.length - 1]));
  const [comMaterial, setComMaterial] = useState(false);
  const [material, setMaterial] = useState<Material>(MATERIAL_VAZIO);
  const criar = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append("meses", String(meses));
      return api.postForm<Detalhe>(`/condominios/${condominioId}/apresentacoes`,
        comMaterial ? paraForm(material, fd) : paraForm(MATERIAL_VAZIO, fd));
    },
    onSuccess: (d) => { setMaterial(MATERIAL_VAZIO); onCriada(d.id); },
  });

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={18} className="text-brand-700" />
        <h3 className="font-semibold text-slate-900">Nova apresentação</h3>
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label className="text-sm text-slate-700">Período:</label>
        <select value={meses} onChange={(e) => setMeses(Number(e.target.value))}
          className="px-3 py-1.5 text-sm border border-slate-300 rounded-md">
          {opcoes.map((m) => <option key={m} value={m}>{m === 1 ? "Último mês" : `Últimos ${m} meses`}</option>)}
        </select>
        <span className="text-xs text-slate-400">({mesesAuditados} meses auditados disponíveis)</span>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <BotaoModo ativo={!comMaterial} onClick={() => setComMaterial(false)}
          titulo="Automática" texto="A IA monta a melhor apresentação com os dados." />
        <BotaoModo ativo={comMaterial} onClick={() => setComMaterial(true)}
          titulo="Com meus tópicos e textos" texto="Você indica até 10 itens; a IA completa." />
      </div>
      {comMaterial && <EditorMaterial condominioId={condominioId} valor={material} onChange={setMaterial} />}
      {criar.isError && <Aviso cor="rose">{msgErro(criar.error)}</Aviso>}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="button" disabled={criar.isPending || (comMaterial && contarItens(material) === 0)}
          onClick={() => criar.mutate()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-50 text-white text-sm font-medium rounded-md">
          {criar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Presentation size={16} />}
          Gerar apresentação
        </button>
        <span className="text-xs text-slate-500">Leva de 1 a 3 minutos. Custo aproximado: US$ 0,25 a 0,40 (entra no Uso & Custos).</span>
      </div>
    </div>
  );
}

function BotaoModo({ ativo, onClick, titulo, texto }: { ativo: boolean; onClick: () => void; titulo: string; texto: string }) {
  return (
    <button type="button" onClick={onClick}
      className={`text-left px-4 py-2.5 rounded-lg border text-sm transition ${ativo
        ? "border-brand-600 bg-brand-50 ring-1 ring-brand-600" : "border-slate-200 hover:border-slate-300"}`}>
      <div className="font-semibold text-slate-900">{titulo}</div>
      <div className="text-xs text-slate-500">{texto}</div>
    </button>
  );
}

// ---------------------------------------------------------------------
// Detalhe: prévia, conferência, ajustes, versões
// ---------------------------------------------------------------------
function DetalheApresentacao({ id, podeEditar, condominioId, onVoltar }: {
  id: number; podeEditar: boolean; condominioId: number; onVoltar: () => void;
}) {
  const qc = useQueryClient();
  const chave = ["apresentacao", id];
  const { data: ap, isLoading } = useQuery<Detalhe>({
    queryKey: chave,
    queryFn: () => api.get(`/apresentacoes/${id}`),
    refetchInterval: (q) => (q.state.data?.status === "gerando" ? 3000 : false),
  });
  const [verNumero, setVerNumero] = useState<number | null>(null);
  const [pedido, setPedido] = useState("");
  const [inserindo, setInserindo] = useState(false);
  const [material, setMaterial] = useState<Material>(MATERIAL_VAZIO);
  const [slideExcluir, setSlideExcluir] = useState(0);

  // Ao surgir uma versão nova, a prévia passa a mostrar a mais recente.
  const atual = ap?.versoes[0];
  useEffect(() => { setVerNumero(null); }, [atual?.numero]);
  const versao = ap?.versoes.find((v) => v.numero === (verNumero ?? atual?.numero)) ?? atual;

  const atualizar = (d: Detalhe) => qc.setQueryData(chave, d);
  const ajustar = useMutation({
    mutationFn: () => api.post<Detalhe>(`/apresentacoes/${id}/ajustar`, { pedido }),
    onSuccess: (d) => { setPedido(""); atualizar(d); },
  });
  const inserir = useMutation({
    mutationFn: () => api.postForm<Detalhe>(`/apresentacoes/${id}/inserir`, paraForm(material, new FormData())),
    onSuccess: (d) => { setMaterial(MATERIAL_VAZIO); setInserindo(false); atualizar(d); },
  });
  const excluirSlide = useMutation({
    mutationFn: (slide: number) => api.post<Detalhe>(`/apresentacoes/${id}/excluir-slide`, { slide }),
    onSuccess: (d) => { setSlideExcluir(0); atualizar(d); },
  });
  const restaurar = useMutation({
    mutationFn: (numero: number) => api.post<Detalhe>(`/apresentacoes/${id}/restaurar`, { numero }),
    onSuccess: atualizar,
  });
  const excluir = useMutation({
    mutationFn: () => api.delete(`/apresentacoes/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["apresentacoes", condominioId] }); onVoltar(); },
  });

  if (isLoading || !ap) return <Loader2 className="animate-spin text-slate-400" />;
  const gerando = ap.status === "gerando";
  const erroAcao = [ajustar, inserir, excluirSlide, restaurar, excluir].find((m) => m.isError)?.error;
  const problemas = versao?.conferencia.filter((c) => c.tipo === "problema") ?? [];
  const avisos = versao?.conferencia.filter((c) => c.tipo === "aviso") ?? [];

  return (
    <div>
      <button type="button" onClick={onVoltar}
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft size={16} /> Voltar às apresentações
      </button>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">{ap.titulo}</h2>
          <div className="text-sm text-slate-500">{ap.periodo_txt} · criada por {ap.criado_por ?? "—"} em {fmtDataHora(ap.criado_em)}</div>
        </div>
        {versao && !gerando && (
          <div className="flex flex-wrap gap-2">
            <a href={apresentacaoHtmlUrl(id, versao.numero)} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-300 rounded-md hover:bg-slate-50">
              <ExternalLink size={15} /> Abrir em tela cheia
            </a>
            <a href={apresentacaoHtmlUrl(id, versao.numero, true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-brand-700 hover:bg-brand-800 text-white text-sm font-medium rounded-md">
              <Download size={15} /> Baixar (HTML)
            </a>
          </div>
        )}
      </div>

      {gerando && (
        <Aviso cor="amber">
          <span className="inline-flex items-center gap-2 font-medium"><Loader2 size={16} className="animate-spin" /> {ap.tarefa ?? "Gerando"}…</span>
          <span className="block text-xs mt-1">Leva de 1 a 3 minutos. Pode sair desta tela; a apresentação continua sendo gerada.</span>
        </Aviso>
      )}
      {ap.erro && !gerando && <Aviso cor="rose"><AlertTriangle size={15} className="inline mr-1" />{ap.erro}</Aviso>}
      {erroAcao && <Aviso cor="rose">{msgErro(erroAcao)}</Aviso>}

      {versao && (
        <>
          <div className="text-xs text-slate-500 mb-1.5">
            Mostrando a versão {versao.numero}{versao.numero !== atual?.numero && " (anterior)"} · {versao.n_slides} slides ·
            clique na prévia e use as setas · tecla N mostra as notas do apresentador · para PDF, abra em tela cheia e use Ctrl+P
          </div>
          <div className="relative w-full rounded-xl overflow-hidden border border-slate-200 bg-slate-900" style={{ aspectRatio: "16 / 9" }}>
            <iframe key={versao.numero} title="Prévia da apresentação" src={apresentacaoHtmlUrl(id, versao.numero)}
              sandbox="allow-scripts allow-popups" allowFullScreen className="absolute inset-0 w-full h-full" />
          </div>

          <div className="mt-3">
            {problemas.length === 0 ? (
              <Aviso cor="emerald">
                <CheckCircle2 size={15} className="inline mr-1" />
                Conferência: todos os valores citados existem nos balancetes auditados.
                {avisos.length > 0 && ` ${avisos.length} valor(es) vieram dos seus documentos (não auditados).`}
              </Aviso>
            ) : (
              <Aviso cor="amber">
                <b>Conferência: {problemas.length} ponto(s) para revisar antes de apresentar.</b>
                <ul className="list-disc ml-5 mt-1 text-xs">
                  {problemas.map((p, i) => <li key={i}>{p.slide ? `Slide ${p.slide}: ` : ""}{p.texto}</li>)}
                </ul>
                <span className="block text-xs mt-1">Corrija com "Pedir alteração" (ex.: "No slide 3, tire o valor X").</span>
              </Aviso>
            )}
          </div>
        </>
      )}

      {podeEditar && versao && (
        <div className="grid gap-4 lg:grid-cols-2 mt-2">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2 font-semibold text-slate-900"><Wand2 size={16} className="text-brand-700" /> Pedir alteração</div>
            <textarea value={pedido} onChange={(e) => setPedido(e.target.value)} rows={4} maxLength={3000} disabled={gerando}
              placeholder={'Ex.: "Exclua o slide 11." · "No slide 8, troque El Niño por chuvas muito acima da média." · "Deixe o slide 3 mais tranquilizador."'}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md" />
            <button type="button" disabled={gerando || !pedido.trim() || ajustar.isPending} onClick={() => ajustar.mutate()}
              className="mt-2 inline-flex items-center gap-2 px-3 py-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-50 text-white text-sm font-medium rounded-md">
              {ajustar.isPending ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} Aplicar alteração
            </button>
            <span className="ml-2 text-xs text-slate-400">Só o que você pedir muda. ~1 minuto, ~US$ 0,20.</span>

            <div className="border-t border-slate-100 mt-4 pt-3">
              <div className="text-sm font-medium text-slate-800 mb-1.5">Excluir um slide (na hora, sem custo)</div>
              <div className="flex gap-2">
                <select value={slideExcluir} onChange={(e) => setSlideExcluir(Number(e.target.value))} disabled={gerando}
                  className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-slate-300 rounded-md">
                  <option value={0}>Escolha o slide…</option>
                  {atual?.slides.map((t, i) => <option key={i} value={i + 1}>{i + 1}. {t}</option>)}
                </select>
                <button type="button" disabled={!slideExcluir || gerando || excluirSlide.isPending}
                  onClick={() => excluirSlide.mutate(slideExcluir)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-rose-300 text-rose-700 rounded-md hover:bg-rose-50 disabled:opacity-50">
                  <Trash2 size={14} /> Excluir
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2 font-semibold text-slate-900"><Plus size={16} className="text-brand-700" /> Inserir tópicos ou textos</div>
            <p className="text-xs text-slate-500 mb-2">Mantém a apresentação como está e só acrescenta slides para os novos itens, no ponto certo da história.</p>
            {inserindo ? (
              <>
                <EditorMaterial condominioId={condominioId} valor={material} onChange={setMaterial} />
                <div className="mt-3 flex gap-2">
                  <button type="button" disabled={gerando || contarItens(material) === 0 || inserir.isPending} onClick={() => inserir.mutate()}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-50 text-white text-sm font-medium rounded-md">
                    {inserir.isPending ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Inserir na apresentação
                  </button>
                  <button type="button" onClick={() => setInserindo(false)} className="px-3 py-2 text-sm text-slate-500">Cancelar</button>
                </div>
              </>
            ) : (
              <button type="button" disabled={gerando} onClick={() => setInserindo(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50">
                <Plus size={15} /> Escolher o que inserir
              </button>
            )}
          </div>
        </div>
      )}

      {ap.versoes.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 mt-4">
          <div className="flex items-center gap-2 mb-2 font-semibold text-slate-900"><History size={16} className="text-brand-700" /> Versões</div>
          <ul className="divide-y divide-slate-100">
            {ap.versoes.map((v) => (
              <li key={v.numero} className="py-2 flex flex-wrap items-start gap-x-3 gap-y-1 text-sm">
                <span className={`font-semibold ${v.numero === versao?.numero ? "text-brand-700" : "text-slate-700"}`}>v{v.numero}</span>
                <div className="flex-1 min-w-[200px]">
                  <div className="text-slate-800">{v.origem_rotulo}{v.pedido ? `: ${v.pedido}` : ""}</div>
                  <div className="text-xs text-slate-400">
                    {v.criado_por ?? "—"} · {fmtDataHora(v.criado_em)} · {v.n_slides} slides
                    {v.uso.custo_estimado_usd != null && ` · US$ ${v.uso.custo_estimado_usd.toFixed(2).replace(".", ",")}`}
                    {v.documentos.length > 0 && ` · textos: ${v.documentos.map((d) => d.nome).join(", ")}`}
                  </div>
                </div>
                <button type="button" onClick={() => setVerNumero(v.numero)} className="text-xs text-brand-700 hover:underline">Ver</button>
                {podeEditar && v.numero !== atual?.numero && (
                  <button type="button" disabled={gerando || restaurar.isPending} onClick={() => restaurar.mutate(v.numero)}
                    className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-brand-700 disabled:opacity-50">
                    <RotateCcw size={12} /> Voltar para esta
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {podeEditar && !gerando && (
        <button type="button"
          onClick={() => { if (window.confirm("Excluir esta apresentação e todas as suas versões?")) excluir.mutate(); }}
          className="mt-4 inline-flex items-center gap-1.5 text-xs text-rose-600 hover:underline">
          <Trash2 size={13} /> Excluir apresentação
        </button>
      )}
    </div>
  );
}
